<template>
  <div class="row m-0">
    <div class="col-auto px-0 pb-3">
      <div class="input-group">
        <RouterLink :to="{ name: 'labels-item' }" class="btn btn-outline-secondary">
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
          {{ $t('action.back') }}
        </RouterLink>
      </div>
    </div>
    <div class="col">
      <h2>{{ $t('page.item-label.captureTitle') }}</h2>
    </div>
  </div>

  <ScanInput ref="scanInput" :instruction="$t('action.scanBarcode')" @scanned="barcodeScanned" class="input-group" />

  <div v-if="error" class="alert alert-danger mt-2">
    {{ error }}
  </div>

  <div v-if="currentItem" :disabled="isLoading" class="pe-1">
    <h3 class="pt-4">
      <ItemField :item="currentItem" />
    </h3>

    <div class="row g-3">

      <div v-if="currentItem?.trackLotNumber" class="col-sm-6 col-md-4 col-lg-3">
        <label for="lot" class="form-label">{{ $t('field.masterdata.lot') }}</label>
        <Typeahead v-model="currentLot" id="lot" ref="lotInput" :inputClass="{ 'form-control': true, 'is-invalid': !isLotValid }" :items="currentItemStockLots" :minInputLength="1" :allowNew="true" :uppercase="true" />
      </div>

      <div v-if="currentItem?.trackBestBeforeDate" class="col-sm-6 col-md-4 col-lg-3">
        <label for="bestBefore" class="form-label">{{ $t('field.masterdata.bestBefore') }}</label>
        <input v-model="currentBestBefore" type="date" class="form-control" id="bestBefore" ref="bestBeforeInput" :min="new Date().toISOString().split('T')[0]" :class="{ 'is-invalid': !isBestBeforeValid }">
      </div>

      <div v-if="currentItem?.trackExpirationDate" class="col-sm-6 col-md-4 col-lg-3">
        <label for="expiry" class="form-label">{{ $t('field.masterdata.expiry') }}</label>
        <input v-model="currentExpiry" type="date" class="form-control" id="expiry" ref="expiryInput" :min="new Date().toISOString().split('T')[0]" :class="{ 'is-invalid': !isExpiryValid }">
      </div>

      <div class="d-grid gap-2">
        <button type="button" @click="addSignatureItemLabel()" class="btn btn-primary btn-lg d-block" :disabled="!isValid || isLoading"><font-awesome-icon :icon="['fas', 'plus']" />  {{ $t('page.item-label.confirm') }}</button>
      </div>

    </div>
  </div>

</template>

<script setup lang="ts">
import ScanInput from "@/components/form/ScanInput.vue";
import { computed, nextTick, ref } from "vue";
import IGS1 from "@/models/IGS1";
import { useItemsStore, useTradeItemsStore, useItemLabelsStore, useStockStore } from "@/stores";
import { useI18n } from "vue-i18n";
import { ItemField } from '@/components/fields';
import FrontendStock from "@/models/frontend/Stock";
import { router, compareOptionalDate, compareOptionalString } from "@/services";
import SignatureItem from "@/models/frontend/SignatureItem";
import Item from "@/models/frontend/Item";

const i18n = useI18n();

const isLoading = ref(false);
const error = ref('');

const currentItem = ref<Item | undefined>();
const currentLot = ref<string | undefined>();
const currentBestBefore = ref<string | undefined>();
const currentExpiry = ref<string | undefined>();
const currentItemStock = ref<FrontendStock[]>();
const currentItemStockLots = ref<string[]>([]);

const scanInput = ref<InstanceType<typeof ScanInput> | null>(null);
const lotInput = ref<HTMLInputElement | null>(null);
const bestBeforeInput = ref<HTMLInputElement | null>(null);
const expiryInput = ref<HTMLInputElement | null>(null);

const itemStore = useItemsStore();
const tradeItemStore = useTradeItemsStore();
const itemLabelsStore = useItemLabelsStore();

const isLotValid = computed(() => {
  if (!currentItem.value) { return false; }
  // return !currentItem.value.trackLotNumber || !!currentLot.value;
  if (currentItem.value.trackLotNumber && !currentLot.value) { return false; }
  if (!currentItem.value.trackLotNumber && !!currentLot.value) { return false; }
  return true;
});

const isBestBeforeValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (!currentItem.value.trackBestBeforeDate) { return true; }
  if (!currentBestBefore.value) { return false; }
  return (new Date(currentBestBefore.value) >= new Date());
});

const isExpiryValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (!currentItem.value.trackExpirationDate) { return true; }
  if (!currentExpiry.value) { return false; }
  return (new Date(currentExpiry.value) >= new Date());
});

const isValid = computed(() => {
  if (!isLotValid.value) { return false; }
  if (!isBestBeforeValid.value) { return false; }
  if (!isExpiryValid.value) { return false; }
  return true;
});

const getDateString = (date: Date | undefined) => {
  if (!date) { return undefined; }
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - (offset * 60 * 1000));
  return date.toISOString().split('T')[0];
}

const barcodeScanned = async (barcode: string, gs1: IGS1 | undefined) => {
  currentItem.value = undefined;
  currentLot.value = undefined;
  currentBestBefore.value = undefined;
  currentExpiry.value = undefined;
  let nextErrorField = '';

  // search item
  if (barcode || gs1) {
    const contentCode = gs1?.gtin ?? barcode;
    var tradeItem = tradeItemStore.getByCode(contentCode);
    if (tradeItem) {
      currentItem.value = itemStore.getById(tradeItem.itemId);
    }
  }

  // if not matched already, try get item by code
  if (!currentItem.value) {
    currentItem.value = itemStore.getByCode(barcode);
  }

  if (!currentItem.value) {
    error.value = i18n.t('error.unknownItem') + ': "' + barcode + '"';
    return;
  }

  error.value = '';

  if (gs1?.lot) {
    currentLot.value = gs1.lot;
  }
  if (!isLotValid.value && !nextErrorField) {
    nextErrorField = 'lot';
  }

  if (gs1?.bestBefore) {
    currentBestBefore.value = getDateString(gs1.bestBefore);
  }
  if (!isBestBeforeValid.value && !nextErrorField) {
    nextErrorField = 'bestBefore';
  }

  if (gs1?.expiry) {
    currentExpiry.value = getDateString(gs1.expiry);
  }
  if (!isExpiryValid.value && !nextErrorField) {
    nextErrorField = 'expiry';
  }

  await fetchCurrentItemStock();
  currentItemStockLots.value = [...new Set(currentItemStock.value?.map((stock) => stock.lot ?? '') ?? [])]; // set is used to remove duplicates

  if (!nextErrorField) {
    await addSignatureItemLabel();
    scanInput.value?.focus();
  }
  else {
    await nextTick(() => {
      if (nextErrorField === 'lot') document.getElementById("lot")?.getElementsByTagName('input')[0]!.focus();
      else if (nextErrorField === 'bestBefore') bestBeforeInput.value?.focus();
      else if (nextErrorField === 'expiry') expiryInput.value?.focus();
    });
  }
};

const addSignatureItemLabel = async () => {
  if (!currentItem.value) { return; }

  var sigMat = new SignatureItem(
    currentItem.value,
    currentItem.value.baseUnitCode,
    1,
    currentLot.value?.toLocaleUpperCase(),
    currentBestBefore.value,
    currentExpiry.value,
    false
  );
  itemLabelsStore.addSignatureItem(sigMat);
  currentItem.value = undefined;
  await router.push({ name: 'labels-item' });
}

const fetchCurrentItemStock = async () => {
  if (!currentItem.value) { return; }
  const stocksStore = useStockStore();
  const itemStock = await stocksStore.fetchAllByItem(currentItem.value.id);
  currentItemStock.value = itemStock.sort((a, b) =>
    a.item.name.localeCompare(b.item.name) ||
    compareOptionalDate(a.expiryAsDate, b.expiryAsDate) ||
    compareOptionalDate(a.bestBeforeAsDate, b.bestBeforeAsDate) ||
    compareOptionalString(a.lot, b.lot)
  );
}
</script>
